import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatCard from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Users as UsersIcon, Clock, TrendingUp, Palmtree, ShieldOff,
  MailWarning, AlertTriangle, CheckCircle2, Stethoscope, Briefcase, UserCheck, UserPlus, Coffee
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const allowed = ["super_admin", "admin", "hr_user", "manager"];
    if (!allowed.includes(user?.role)) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (user?.role === "employee") {
    return (
      <div className="space-y-6" data-testid="employee-dashboard">
        <div>
          <div className="tiny-label">Your workspace</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Hi {user.full_name.split(" ")[0]}</h1>
          <p className="text-muted-foreground mt-1">Log your hours and review your holiday balance.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <div className="tiny-label">Time entry</div>
            <p className="mt-2 text-sm text-muted-foreground">Submit your daily hours for the open period.</p>
            <Button className="mt-4" onClick={() => navigate("/my-hours")} data-testid="go-my-hours">Open my hours</Button>
          </CardContent></Card>
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <div className="tiny-label">Profile & 2FA</div>
            <p className="mt-2 text-sm text-muted-foreground">Protect your account with 2-factor authentication.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/profile")} data-testid="go-profile">Manage profile</Button>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="tiny-label">Overview · week of {data?.week_start || "…"}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Workforce pulse</h1>
          <p className="text-muted-foreground mt-2">Active staff, hours logged, approvals pending, and security posture at a glance.</p>
        </div>
      </div>

      {loading && <div className="text-muted-foreground text-sm">Loading…</div>}

      {data && (
        <>
          {!data.smtp_configured && (
            <Alert className="border-amber-200 bg-amber-50 text-amber-800" data-testid="smtp-warning">
              <MailWarning className="w-4 h-4" />
              <AlertTitle>SMTP not configured</AlertTitle>
              <AlertDescription>
                Password resets, 2FA emails, and HR notifications cannot be sent until SMTP is configured.
                <Button variant="link" className="px-1 text-amber-900" onClick={() => navigate("/settings")} data-testid="smtp-warning-go">Open settings →</Button>
              </AlertDescription>
            </Alert>
          )}
          {data.failed_logins_24h > 10 && (
            <Alert className="border-red-200 bg-red-50 text-red-800" data-testid="security-alert">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>Unusual login activity</AlertTitle>
              <AlertDescription>{data.failed_logins_24h} failed sign-in attempts in the last 24 hours. Review the audit log.</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testId="stat-active-employees" label="Active employees" value={data.active_employees} sub={`of ${data.total_employees} total`} icon={UsersIcon} />
            <StatCard testId="stat-hours-week" label="Hours this week" value={data.hours_this_week.toFixed(1)} sub="across all employees" icon={Clock} />
            <StatCard testId="stat-overtime-week" label="Overtime this week" value={data.overtime_this_week.toFixed(1)} sub="hours above standard" icon={TrendingUp} tone={data.overtime_this_week > 0 ? "warning" : "default"} />
            <StatCard testId="stat-holidays-ytd" label="Holiday YTD" value={data.holidays_taken_ytd} sub="days taken this year" icon={Palmtree} />
            <StatCard testId="stat-dayoffs-ytd" label="Day Off YTD" value={data.day_offs_taken_ytd ?? 0} sub="days taken this year" icon={Coffee} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testId="stat-pending-time" label="Time approvals" value={data.pending_time_approvals} sub="submitted by employees" icon={CheckCircle2} tone={data.pending_time_approvals > 0 ? "info" : "default"} />
            <StatCard testId="stat-pending-ot" label="Overtime approvals" value={data.pending_overtime_approvals} sub="awaiting decision" icon={TrendingUp} tone={data.pending_overtime_approvals > 0 ? "info" : "default"} />
            <StatCard testId="stat-no-2fa" label="Users without 2FA" value={data.users_without_2fa} sub={`of ${data.total_users} active accounts`} icon={ShieldOff} tone={data.users_without_2fa > 0 ? "warning" : "success"} />
            <StatCard testId="stat-failed-logins" label="Failed logins (24h)" value={data.failed_logins_24h} sub="tracked in audit log" icon={AlertTriangle} tone={data.failed_logins_24h > 5 ? "error" : "default"} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-leave-widgets">
            <StatCard testId="stat-on-holiday" label="On holiday today" value={data.on_holiday_today ?? 0} sub="approved leave" icon={Palmtree} tone={(data.on_holiday_today ?? 0) > 0 ? "info" : "default"} onClick={() => navigate("/holidays?status=approved")} />
            <StatCard testId="stat-on-sick" label="On sick leave today" value={data.on_sick_today ?? 0} sub="approved" icon={Stethoscope} tone={(data.on_sick_today ?? 0) > 0 ? "warning" : "default"} onClick={() => navigate("/sick-leave?tab=approved")} />
            <StatCard testId="stat-on-dayoff" label="On day off today" value={data.on_day_off_today ?? 0} sub="approved" icon={Coffee} tone={(data.on_day_off_today ?? 0) > 0 ? "info" : "default"} />
            <StatCard testId="stat-pending-hol" label="Pending holiday requests" value={data.pending_holiday_requests ?? 0} sub="awaiting approval" icon={UserCheck} tone={(data.pending_holiday_requests ?? 0) > 0 ? "info" : "default"} onClick={() => navigate("/holidays?status=pending")} />
            <StatCard testId="stat-pending-sick" label="Pending sick requests" value={data.pending_sick_requests ?? 0} sub="awaiting approval" icon={UserCheck} tone={(data.pending_sick_requests ?? 0) > 0 ? "info" : "default"} onClick={() => navigate("/sick-leave?tab=pending")} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dash-workforce-split">
            <StatCard testId="stat-full-time" label="Full-time employees" value={data.full_time_employees ?? 0} sub="active" icon={Briefcase} />
            <StatCard testId="stat-part-time" label="Part-time employees" value={data.part_time_employees ?? 0} sub="active" icon={Briefcase} />
            <StatCard testId="stat-setup-incomplete" label="Setup incomplete" value={data.setup_incomplete ?? 0} sub="need employment type / hours" icon={UserPlus} tone={(data.setup_incomplete ?? 0) > 0 ? "warning" : "success"} />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-border shadow-none"><CardContent className="p-6">
              <div className="tiny-label">Quick actions</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => navigate("/time-tracking")} data-testid="qa-log-time">Log time</Button>
                <Button variant="outline" onClick={() => navigate("/weekly-hours")} data-testid="qa-weekly">Weekly hours</Button>
                <Button variant="outline" onClick={() => navigate("/overtime")} data-testid="qa-overtime">Overtime</Button>
                <Button variant="outline" onClick={() => navigate("/quick-hours")} data-testid="qa-quick">Quick hours</Button>
                <Button variant="outline" onClick={() => navigate("/holidays")} data-testid="qa-holidays">Holidays</Button>
                <Button variant="outline" onClick={() => navigate("/sick-leave")} data-testid="qa-sick">Sick leave</Button>
                <Button variant="outline" onClick={() => navigate("/reports")} data-testid="qa-reports">Reports</Button>
              </div>
            </CardContent></Card>
            <Card className="border-border shadow-none"><CardContent className="p-6">
              <div className="tiny-label">Security posture</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li className={`flex items-center gap-2 ${data.users_without_2fa === 0 ? "text-emerald-700" : "text-amber-700"}`}>
                  <ShieldOff className="w-4 h-4" /> {data.users_without_2fa === 0 ? "All users have 2FA enabled" : `${data.users_without_2fa} users without 2FA`}
                </li>
                <li className={`flex items-center gap-2 ${data.smtp_configured ? "text-emerald-700" : "text-amber-700"}`}>
                  <MailWarning className="w-4 h-4" /> {data.smtp_configured ? "SMTP configured" : "SMTP not configured"}
                </li>
              </ul>
            </CardContent></Card>
          </div>
        </>
      )}
    </div>
  );
}
